/**
 * Site-wide theme switcher.
 *
 * Three preview palettes ship as `[data-theme="..."]` blocks in
 * index.css; this module is the runtime that reads/writes which one
 * is active. The user toggles between them on the landing page;
 * once they pick, every other surface inherits via the
 * `data-theme` attribute on `<html>`.
 *
 * Storage key is intentionally short + scoped — we don't reach for
 * a settings store yet; one string in localStorage is enough.
 */

/** `lab` is now baked into `:root`, so it's the value `loadTheme`
 *  returns when the localStorage key is empty. The other ids
 *  apply via the `data-theme` attribute. */
export const THEME_IDS = ['lab', 'paper', 'riso', 'default'] as const;
export type ThemeId = (typeof THEME_IDS)[number];

export interface ThemeOption {
  id: ThemeId;
  name: string;
  /** One-line vibe — shown in tooltips + the chip's helper line. */
  blurb: string;
}

export const THEME_OPTIONS: ThemeOption[] = [
  {
    id: 'lab',
    name: 'Lab notebook',
    blurb: 'Graph-paper green, ink blue, citrus + magenta. (Default.)',
  },
  {
    id: 'paper',
    name: 'Studio paper',
    blurb: 'Cream paper, ink, lemon + coral. Sketchbook feel.',
  },
  {
    id: 'riso',
    name: 'Risograph',
    blurb: 'Eggplant dark, mint + apricot. Printed-zine feel.',
  },
  {
    id: 'default',
    name: 'Original',
    blurb: 'The dark navy + amber palette we shipped first.',
  },
];

const STORAGE_KEY = 'bl.theme';

/** Read the persisted theme. Falls back to `lab` if the key is
 *  missing or holds a value the current build no longer ships. */
export function loadTheme(): ThemeId {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && (THEME_IDS as readonly string[]).includes(raw)) {
      return raw as ThemeId;
    }
  } catch {
    // localStorage can throw in privacy-mode iframes; ignore.
  }
  return 'lab';
}

/** Apply a theme to the document + persist the choice. The `lab`
 *  theme intentionally clears `data-theme` so the `:root` block
 *  in index.css applies as the (default) fallback — alternates
 *  override via the `[data-theme="..."]` selectors. */
export function setTheme(id: ThemeId): void {
  if (id === 'lab') {
    document.documentElement.removeAttribute('data-theme');
  } else {
    document.documentElement.setAttribute('data-theme', id);
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // Ignore quota / privacy-mode failures — the in-memory attr is
    // still set, so the current session looks right even if the
    // pick doesn't survive a reload.
  }
}

/** Boot-time apply, called once from App. Reads localStorage and
 *  pushes the matching attribute onto `<html>` before paint. */
export function initTheme(): ThemeId {
  const id = loadTheme();
  setTheme(id);
  return id;
}
